/**
 * Première — Date de livraison estimée
 * Porté de tb-shopify-web-v2/assets/delivery-date-handler.js (ENG-3415).
 *
 * Calcule une fourchette de dates en JOURS OUVRÉS (exclut week-ends + fériés FR fixes)
 * à partir du metafield produit `custom.delivery_days` (nb de jours), et :
 *  - hydrate les éléments [data-delivery-days] (sans data-delivery-prop) avec le texte localisé ;
 *  - remplit les hidden inputs [data-delivery-prop] avec la phrase lisible (ex. "entre le 5 et le 10 juin"),
 *    posée en line item property cachée `_` → visible par les OPS sur la commande + affichée telle quelle
 *    dans l'email de confirmation (locale de l'acheteur au moment de l'ajout au panier) ;
 *  - remplit les hidden inputs [data-delivery-vendor-prop] avec "{vendor}: {JJ/MM/AAAA}" (borne max du
 *    créneau), posés en property cachée `_Estimated delivery date` (ENG-3836) — même clé et même format
 *    que les commandes The Bradery et l'app mobile, pour un affichage admin identique entre boutiques.
 *
 * Si delivery_days est vide/invalide : on laisse le fallback Liquid en place et on neutralise
 * l'éventuel input (retrait de l'attribut name) pour ne pas envoyer de propriété vide.
 */

const FIXED_HOLIDAYS = ['01-01', '05-01', '05-08', '07-14', '08-15', '11-01', '11-11', '12-25'];

function isHoliday(date) {
  return FIXED_HOLIDAYS.includes(date.toISOString().slice(5, 10));
}

function addBusinessDays(date, daysToAdd) {
  const d = new Date(date);
  let left = daysToAdd;
  let iterations = 0;
  while (left > 0 && iterations < 100) {
    iterations += 1;
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6 && !isHoliday(d)) left -= 1;
  }
  return d;
}

function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatRange(minDate, maxDate, locale) {
  const t = (typeof Theme !== 'undefined' && Theme && Theme.translations) || {};
  const opts = { day: 'numeric', month: 'long' };
  if (minDate.getMonth() === maxDate.getMonth()) {
    const tpl = t.delivery_between_same_month || 'entre le {{ min }} et le {{ max }} {{ month }}';
    return tpl
      .replace('{{ min }}', minDate.getDate())
      .replace('{{ max }}', maxDate.getDate())
      .replace('{{ month }}', maxDate.toLocaleDateString(locale, { month: 'long' }));
  }
  const tpl = t.delivery_between_diff_month || 'entre le {{ min_date }} et le {{ max_date }}';
  return tpl
    .replace('{{ min_date }}', minDate.toLocaleDateString(locale, opts))
    .replace('{{ max_date }}', maxDate.toLocaleDateString(locale, opts));
}

/**
 * @param {string|number} deliveryDays - nb de jours (metafield custom.delivery_days)
 * @param {Date} [fromDate] - date de départ (défaut: maintenant)
 * @param {string} [locale]
 * @returns {{minISO:string,maxISO:string,displayText:string}|null}
 */
export function computeRange(deliveryDays, fromDate, locale) {
  const days = parseInt(deliveryDays, 10);
  if (!days || days < 1) return null;
  const start = fromDate || new Date();
  const loc = locale || document.documentElement.lang || 'fr';
  const minDate = addBusinessDays(start, Math.max(1, days - 2));
  const maxDate = addBusinessDays(start, days + 1);
  return { minISO: toISO(minDate), maxISO: toISO(maxDate), displayText: formatRange(minDate, maxDate, loc) };
}

export function hydrate(root = document) {
  const locale = document.documentElement.lang || 'fr';
  root.querySelectorAll('[data-delivery-days]').forEach((el) => {
    const range = computeRange(el.getAttribute('data-delivery-days'), new Date(), locale);
    if (!range) {
      if (el.hasAttribute('data-delivery-prop') || el.hasAttribute('data-delivery-vendor-prop')) {
        el.removeAttribute('name');
      }
      return;
    }
    if (el.hasAttribute('data-delivery-vendor-prop')) {
      const vendor = el.getAttribute('data-vendor') || '';
      el.value = vendor + ': ' + range.maxISO.split('-').reverse().join('/');
    } else if (el.hasAttribute('data-delivery-prop')) {
      el.value = range.displayText;
    } else {
      el.textContent = range.displayText;
    }
  });
}

function init() {
  hydrate(document);
  // Re-hydrate après mise à jour panier (nom d'event à confirmer vs component-cart-items.js)
  document.addEventListener('cart:update', () => hydrate(document));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
